import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faTrash,
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faImage,
  faChevronDown,
  faChevronUp,
  faBan,
  faVirus,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateTime } from "../../utils/DateTimeUtil";
import "./css/ImageAnalysisDetails.css";
import "../../css/Common.css";
import LinkAnalysisSection from "../common/LinkAnalysisSection";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import ThreatBannerShareButton from "../common/ThreatBannerShareButton";

const EXPLANATION_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "h3",
  "h4",
  "h5",
];

/**
 * Sanitize HTML content, handling double-encoded entities from legacy data.
 */
const sanitizeHtml = (html, allowedTags) => {
  if (!html) return "";
  const config = { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: ["style"] };
  let cleaned = DOMPurify.sanitize(html, config);

  // Detect double-encoded HTML (e.g. &lt;p&gt; stored in DB from escapeHtml)
  if (cleaned.includes("&lt;") && cleaned.includes("&gt;")) {
    const temp = document.createElement("div");
    temp.innerHTML = cleaned;
    cleaned = DOMPurify.sanitize(
      temp.textContent || temp.innerText || "",
      config,
    );
  }

  return cleaned;
};

/**
 * Component to display detailed analysis of a single image
 */
const ImageAnalysisDetails = ({ analysis, onBack, onDelete }) => {
  /**
   * Format scam type
   */
  const formatScamType = (scamType) => {
    if (!scamType) return "Unknown";
    return scamType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  /**
   * Format date - using centralized DateTimeUtil
   */
  const formatDate = (dateString) => {
    return formatDateTime(dateString);
  };

  /**
   * Get confidence level
   */
  const getConfidenceLevel = (confidence) => {
    if (confidence >= 0.9) return { label: "Very High", class: "very-high" };
    if (confidence >= 0.75) return { label: "High", class: "high" };
    if (confidence >= 0.5) return { label: "Medium", class: "medium" };
    if (confidence >= 0.25) return { label: "Low", class: "low" };
    return { label: "Very Low", class: "very-low" };
  };

  /**
   * Get scam type icon
   */
  const getScamTypeIcon = (scamType) => {
    const icons = {
      PHISHING: "🎣",
      QR_CODE_SCAM: "📱",
      FAKE_INVOICE: "🧾",
      IMPERSONATION: "🎭",
      LOTTERY_SCAM: "🎰",
      ROMANCE_SCAM: "💔",
      INVESTMENT_SCAM: "💰",
      FINANCIAL_FRAUD: "💳",
      TECH_SUPPORT_SCAM: "💻",
      MALWARE: "🦠",
      SOCIAL_ENGINEERING: "🧠",
      UNKNOWN: "❓",
    };
    return icons[scamType] || "⚠️";
  };

  // Extract analysisResult for easier access
  const analysisResult = analysis.analysisResult || {};
  const confidence = analysisResult.confidence || 0;
  const scamType = analysisResult.scamType;
  const analysisExplanation = analysisResult.explanation;
  const riskFactors = analysisResult.riskFactors || [];
  const suggestions = analysisResult.suggestions;
  const isScam = analysisResult.isScam === true;

  // Check for security failure
  const securityVerdict =
    analysis.securityVerdict || analysisResult.securityVerdict;
  const isSecurityFailure = securityVerdict === "FAIL";
  const hasMalware = analysis.hasMalware || analysisResult.hasMalware || false;
  const processingError =
    analysis.processingError || analysisResult.processingError;

  const confidenceLevel = getConfidenceLevel(confidence);
  const [imageDetailsCollapsed, setImageDetailsCollapsed] = useState(true);
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const [imagePreviewCollapsed, setImagePreviewCollapsed] = useState(true);
  const [localUrlAnalyses, setLocalUrlAnalyses] = useState(
    analysisResult.urlAnalyses || null,
  );

  useEffect(() => {
    // Server-side AI now performs conditional URL following for extracted/ OCR text
    // and will include `urlAnalyses` in the analysis results when applicable.
    // Keep `localUrlAnalyses` available for backwards-compatibility if needed.
  }, [analysisResult]);

  /**
   * Extract recommendations from explanation text
   * Looks for sections like "### Recommendations", "### Safety Recommendations", "### Suggestions"
   * Falls back to the suggestions field if not found
   */
  const extractRecommendations = () => {
    if (!analysisExplanation) {
      return suggestions || null;
    }

    // Try to find recommendations section in the explanation
    const patterns = [
      /###\s*(?:Safety\s+)?Recommendations?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /###\s*Suggestions?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /\*\*(?:Safety\s+)?Recommendations?\*\*\s*\n([\s\S]*?)(?=\n\*\*|\n\n\*\*|$)/i,
    ];

    for (const pattern of patterns) {
      const match = analysisExplanation.match(pattern);
      if (match && match[1]) {
        const extracted = match[1].trim();
        // Clean up any trailing markdown or extra whitespace
        return extracted.replace(/\n{3,}/g, "\n\n");
      }
    }

    // Fallback to suggestions field
    return suggestions || null;
  };

  const recommendations = extractRecommendations();

  return (
    <div className="common-content analysis-details-container">
      {/* Analysis Details Section */}
      <div className="common-section">
        <div className="common-section-header">
          <h3>
            <FontAwesomeIcon icon={faShieldAlt} /> Analysis Details
          </h3>
        </div>

        {/* Threat Assessment Banner - Moved inside Analysis Details */}
        <div
          className={`threat-banner ${
            isSecurityFailure
              ? "security-failed"
              : isScam
                ? "threat-detected"
                : "safe"
          }`}
        >
          <div className="threat-icon">
            <FontAwesomeIcon
              icon={
                isSecurityFailure
                  ? hasMalware
                    ? faVirus
                    : faBan
                  : isScam
                    ? faExclamationTriangle
                    : faCheckCircle
              }
            />
          </div>
          <div className="threat-content">
            <h2 className="threat-title">
              {isSecurityFailure
                ? hasMalware
                  ? "MALWARE DETECTED"
                  : "SECURITY CHECK FAILED"
                : isScam
                  ? "SCAM DETECTED"
                  : "NO THREATS DETECTED"}
            </h2>
            <p className="threat-subtitle">
              {isSecurityFailure
                ? hasMalware
                  ? "This file contains malicious content and has been blocked for your safety"
                  : "This image failed security checks and cannot be displayed"
                : isScam
                  ? "This image contains suspicious or fraudulent content"
                  : "This image appears to be safe"}
            </p>
          </div>
          <ThreatBannerShareButton
            scamDetected={isScam === true || (isSecurityFailure && hasMalware)}
            threatType="Image Scam"
            threatTitle={
              hasMalware ? "Malware Detected in Image" : "Image Scam Detected"
            }
            threatDetail={
              hasMalware
                ? "This file contains malicious content"
                : "This image contains suspicious or fraudulent content"
            }
          />
        </div>

        {/* Security failure notice */}
        {isSecurityFailure && (
          <div className="security-failure-notice">
            <div className="security-notice-icon">
              <FontAwesomeIcon icon={faUserShield} size="2x" />
            </div>
            <div className="security-notice-content">
              <h4>Image Blocked for Your Safety</h4>
              <p>
                {hasMalware
                  ? "This file was detected as containing malware and has been quarantined. The image will not be displayed to protect your device."
                  : "This image failed our security screening and cannot be displayed. This may be due to inappropriate content, an invalid file format, or other security concerns."}
              </p>
              {processingError && (
                <p className="error-detail">
                  <strong>Reason:</strong> {processingError}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Scam Type (if scam and not Unknown) */}
        {isScam &&
          scamType &&
          (() => {
            const normalized = scamType.toLowerCase().replace(/[\s_/]+/g, "");
            return (
              normalized !== "unknown" &&
              normalized !== "other" &&
              normalized !== "otheruncategorized"
            );
          })() && (
            <div
              className="scam-type-display"
              style={{ marginTop: "var(--spacing-md, 12px)" }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} /> Scam Type:{" "}
              <span className="scam-type-badge">
                {formatScamType(scamType)}
              </span>
            </div>
          )}

        {/* Suggestions - Moved before Explanation */}
        {recommendations && (
          <div className="suggestions-section">
            <h4 className="subsection-title">
              <FontAwesomeIcon icon={faInfoCircle} /> What Should You Do?
            </h4>
            <SuggestionsBulletList text={recommendations} />
          </div>
        )}

        {/* Analysis Explanation - Collapsible */}
        {analysisExplanation && (
          <div className="explanation-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => setExplanationCollapsed(!explanationCollapsed)}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon icon={faInfoCircle} /> Explanation
              <FontAwesomeIcon
                icon={explanationCollapsed ? faChevronDown : faChevronUp}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {!explanationCollapsed && (
              <div
                className="explanation-text"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    analysisExplanation,
                    EXPLANATION_ALLOWED_TAGS,
                  ),
                }}
              />
            )}
          </div>
        )}

        {/* Link analysis from OCR/extracted text (client-side follow) */}
        <LinkAnalysisSection
          urlAnalyses={analysisResult.urlAnalyses || localUrlAnalyses}
        />

        {/* Image Preview - Collapsible, at bottom below Explanation */}
        {!isSecurityFailure && (
          <div className="image-preview-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => setImagePreviewCollapsed(!imagePreviewCollapsed)}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon icon={faImage} /> Image Preview
              <FontAwesomeIcon
                icon={imagePreviewCollapsed ? faChevronDown : faChevronUp}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {!imagePreviewCollapsed && (
              <div className="image-display">
                {analysis.imageUrl ? (
                  <img
                    src={analysis.imageUrl}
                    alt={analysis.fileName}
                    className="analysis-image"
                    onClick={() => setImagePreviewCollapsed(true)}
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <div className="image-placeholder">
                    <FontAwesomeIcon icon={faImage} size="3x" />
                    <p>Image not available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageAnalysisDetails;
